import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { auth as authApi, invalidateApiCache } from '../utils/api'
import { userHasModule } from '../config/modules'
const AuthContext = createContext(null)
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const refreshingUser = useRef(false)
  const [features, setFeatures] = useState({ crm:{enabled:true}, orcamentos:{enabled:true}, inteligencia:{enabled:true}, experiencia:{enabled:true} })
  const login = async (email, senha, mfa = '') => {
    try {
      const r = await authApi.login(email, senha, mfa)
      const { user: u } = r.data
      setUser(u); return { ok: true }
    } catch(e) {
      console.error('[AUTH LOGIN ERROR]', e.response?.status, e.response?.data, e)
      const detail = e.response?.data?.detail
      if (detail?.code === 'MFA_REQUIRED') return { ok: false, mfaRequired: true, msg: detail.message }
      const isTimeout = e.code === 'ECONNABORTED' || String(e.message || '').toLowerCase().includes('timeout')
      const fallbackMsg = isTimeout
        ? 'O servidor demorou para responder. Verifique sua conexão e tente novamente.'
        : `Erro ao fazer login (${e.response?.status || e.message})`
      return { ok: false, msg: typeof detail === 'string' ? detail : detail?.message || fallbackMsg }
    }
  }
  const logout = async () => {
    try { await authApi.logout() } finally { invalidateApiCache({sessionChanged:true}); navigator.serviceWorker?.controller?.postMessage({type:'CLEAR_USER_DATA'}); setUser(null) }
  }
  const refreshMe = async () => {
    const r = await authApi.me()
    setUser(r.data)
    return r.data
  }
  const updateLocalUser = u => {
    setUser(u)
  }
  const role = user?.role
  const moduleSignature = user?.modulos_permitidos?.join('|') || ''
  useEffect(() => {
    let active = true
    const fallbackTimer = setTimeout(() => {
      if (active) setLoading(false)
    }, 4500)
    authApi.bootstrap()
      .then(response => { if (active) setUser(response.data) })
      .catch(() => { if (active) setUser(null) })
      .finally(() => {
        clearTimeout(fallbackTimer)
        if (active) setLoading(false)
      })
    return () => {
      active = false
      clearTimeout(fallbackTimer)
    }
  }, [])
  useEffect(() => {
    if (!user?.id) return undefined
    const syncUser = async () => {
      if (document.visibilityState !== 'visible' || refreshingUser.current) return
      refreshingUser.current = true
      try {
        const response = await authApi.me()
        setUser(current => current?.id === response.data.id ? response.data : current)
      } catch (error) {
        if (error.response?.status === 401) setUser(null)
      } finally {
        refreshingUser.current = false
      }
    }
    const onVisibilityChange = () => { if (document.visibilityState === 'visible') syncUser() }
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', syncUser)
    const interval = window.setInterval(syncUser, 30_000)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', syncUser)
      window.clearInterval(interval)
    }
  }, [user?.id])
  useEffect(() => {
    if (!user) return
    authApi.features().then(response => setFeatures(response.data)).catch(() => {})
  }, [user?.id, moduleSignature])
  return (
    <AuthContext.Provider value={{ user, login, logout, loading, refreshMe, updateLocalUser, features,
      hasModule: module => userHasModule(user, module),
      isAdmin: role === 'admin', isFinanceiro: role === 'financeiro',
      isTecnico: ['admin','tecnico'].includes(role),
      isCliente: role === 'cliente' }}>
      {children}
    </AuthContext.Provider>
  )
}
export const useAuth = () => useContext(AuthContext)
